import type {ExtractPropsWithoutRef} from '@/types';

import {useTheme} from '@emotion/react';
import styled from '@emotion/styled';
import {forwardRef, useCallback, useState} from 'react';

import {Body} from '@/components/MapPin/components/Body';
import {Container} from '@/components/MapPin/components/Container';

type Props = ExtractPropsWithoutRef<typeof Container> & {
  selected?: boolean;
  splitView?: boolean;
  style?: React.CSSProperties;
  children: string;
  isHovered?: boolean;
  isPromoted: boolean;
};

export const MapPin = styled(
  forwardRef<HTMLDivElement, Props>(function Pin(
    {style, children, isHovered, selected, splitView, isPromoted, ...restProps},
    ref
  ) {
    const [hovering, setHovering] = useState(false);
    const theme = useTheme();

    const handleMouseEnter = useCallback(() => {
      setHovering(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setHovering(false);
    }, []);

    const highLighted = splitView
      ? isHovered || hovering || selected
      : selected;

    const getBackgroundColor = () => {
      if (isPromoted) {
        return theme.colors.brandA;
      }

      if (highLighted) {
        return theme.colors.interactionSecondary;
      }

      return theme.colors.backgroundPrimary;
    };

    const getTextColor = () => {
      if (isPromoted || highLighted) {
        return theme.colors.contentInversePrimary;
      }

      return theme.colors.contentPrimary;
    };

    return (
      <Container
        {...restProps}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          ...style,
          '--color': getTextColor(),
          '--background': getBackgroundColor(),
        }}
      >
        <Body>{children}</Body>
      </Container>
    );
  })
)``;
